import { bootstrapApplication } from '@angular/platform-browser';
import {
  enableProdMode,
  importProvidersFrom,
  ApplicationConfig,
  provideZoneChangeDetection
} from '@angular/core';
import { provideRouter, withHashLocation } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS
} from '@angular/common/http';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { ToastrModule } from 'ngx-toastr';

// App imports
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { environment } from './environments/environment';

// Services and Interceptors
import { LoaderService } from './app/core/services/loader.service';
import { ApiInterceptor } from './app/core/interceptors/api.interceptor';
import { LoaderInterceptor } from './app/core/interceptors/loader.interceptor';

// Enable production mode
if (environment.production) {
  enableProdMode();
}

// Application configuration using modern Angular 19 patterns
export const appConfig: ApplicationConfig = {
  providers: [
    // Angular 21 is zoneless by default; the app relies on zone.js change detection.
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Router with hash location strategy (matching the NgModule config)
    provideRouter(routes, withHashLocation()),

    // Modern async animations provider (Angular 19)
    provideAnimationsAsync(),

    // HTTP client with fetch API and interceptor support
    provideHttpClient(withFetch(), withInterceptorsFromDi()),

    // Feature modules (using importProvidersFrom for modules that need .forRoot())
    importProvidersFrom(
      NgIdleKeepaliveModule.forRoot(),
      ToastrModule.forRoot({
        timeOut: 3500,
        positionClass: 'toast-top-right',
        preventDuplicates: true,
        progressBar: true,
        progressAnimation: 'increasing',
        closeButton: true
      })
    ),

    // PrimeNG theming (Angular 19 pattern)
    providePrimeNG({
      theme: {
        preset: Aura,
        options: { darkModeSelector: '.p-dark' }
      }
    }),

    // Application services
    LoaderService,

    // HTTP Interceptors
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true
    },

    // Configuration tokens
    {
      provide: 'BASE_API_URL',
      useValue: environment.apiUrl || 'http://localhost:3000'
    }
  ]
};

// Bootstrap the standalone application
bootstrapApplication(AppComponent, appConfig).catch((err) =>
  console.error('Error starting app:', err)
);
